import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Crown,
  UploadCloud,
  Loader2,
  Save,
  Play,
  CheckCircle2,
  ImageIcon,
  Video,
  Calendar,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

// -----------------------------------------------------------------------
// KingdomHourAdmin — dedicated panel to manage the 12-part Kingdom Hour
// series. Admin can upload MP4 videos + thumbnails, edit metadata, set an
// air date, and mark the featured episode.
// -----------------------------------------------------------------------
export default function KingdomHourAdmin() {
  const [show, setShow] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/tv/kingdom-hour");
      setShow(data.show);
      setEpisodes(data.episodes || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleEpisodeUpdate = (id, patch) => {
    setEpisodes((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };

  if (loading) {
    return (
      <section className="border border-gold-500/20 bg-ink-paper/40 p-6 flex items-center gap-3" data-testid="kingdom-hour-admin">
        <Loader2 size={16} className="animate-spin text-gold-400" />
        <p className="text-white/60 text-sm">Loading Kingdom Hour…</p>
      </section>
    );
  }

  if (!show) return null;

  const uploadedCount = episodes.filter((e) => e.video_url).length;

  return (
    <section
      className="border border-gold-500/25 bg-ink-paper/40 p-6"
      style={{
        background:
          "linear-gradient(135deg, rgba(11,32,83,0.35) 0%, rgba(11,15,26,0.6) 100%)",
      }}
      data-testid="kingdom-hour-admin"
    >
      <div className="flex items-center gap-3 mb-2">
        <Crown size={22} className="text-gold-500" />
        <h3 className="font-serif text-2xl text-white">The Kingdom Hour</h3>
        <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-gold-400/80" data-testid="kh-admin-progress">
          {uploadedCount} / {episodes.length} uploaded
        </span>
      </div>
      <p className="text-white/60 text-sm mb-6">
        Manage all twelve episodes of{" "}
        <span className="text-gold-300 italic">"{show.title} — {show.tagline}"</span>. Upload the
        video for each slot, add an air date, and the site cycles them one per day automatically.
      </p>

      <div className="grid gap-4">
        {episodes.map((ep) => (
          <EpisodeRow
            key={ep.id}
            ep={ep}
            onUpdate={(patch) => handleEpisodeUpdate(ep.id, patch)}
            onReload={load}
          />
        ))}
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------
function EpisodeRow({ ep, onUpdate, onReload }) {
  const [draft, setDraft] = useState({
    title: ep.title || "",
    description: ep.description || "",
    air_date: ep.air_date || "",
    is_featured: !!ep.is_featured,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null); // 'video' | 'thumbnail' | null
  const [progress, setProgress] = useState(0);

  const dirty =
    draft.title !== (ep.title || "") ||
    draft.description !== (ep.description || "") ||
    draft.air_date !== (ep.air_date || "") ||
    draft.is_featured !== !!ep.is_featured;

  const saveMeta = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/tv/admin/episodes/${ep.id}`, draft);
      onUpdate(data);
      toast.success(`Episode ${ep.episode_number} saved`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (file, kind) => {
    if (!file) return;
    setUploading(kind);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await api.post("/tv/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
        timeout: 10 * 60 * 1000, // 10 min for large videos
      });
      // Persist the URL on the episode
      const patchKey = kind === "video" ? "video_url" : "thumbnail";
      const patchBody = { [patchKey]: data.url };
      if (kind === "video") patchBody.video_type = "mp4";
      const { data: updated } = await api.patch(`/tv/admin/episodes/${ep.id}`, patchBody);
      onUpdate(updated);
      toast.success(`Episode ${ep.episode_number} — ${kind} uploaded`);
      onReload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(null);
      setProgress(0);
    }
  };

  const isReady = !!ep.video_url;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid={`kh-admin-row-${ep.episode_number}`}
      className={`border ${isReady ? "border-gold-500/40" : "border-white/10"} bg-ink/60 grid md:grid-cols-[140px_1fr] gap-4 p-4`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden border border-gold-500/20 bg-ink">
        {ep.thumbnail && (
          <img src={ep.thumbnail} alt="" className="w-full h-full object-cover" />
        )}
        <div className="absolute top-1.5 left-1.5 inline-flex items-center justify-center w-7 h-7 bg-royal-900/90 border border-gold-500/40 font-serif text-[11px] text-gold-300">
          {String(ep.episode_number).padStart(2, "0")}
        </div>
        {isReady && (
          <div className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 bg-gold-500 text-ink text-[9px] uppercase tracking-[0.18em] font-semibold px-1.5 py-0.5">
            <CheckCircle2 size={9} /> Ready
          </div>
        )}
        <label
          className="absolute inset-x-0 bottom-0 bg-black/70 backdrop-blur-sm text-white text-[9px] uppercase tracking-[0.2em] text-center py-1 cursor-pointer hover:bg-gold-500 hover:text-ink transition-colors"
          data-testid={`kh-admin-thumb-upload-${ep.episode_number}`}
        >
          {uploading === "thumbnail" ? (
            <span className="inline-flex items-center justify-center gap-1"><Loader2 size={9} className="animate-spin" /> {progress}%</span>
          ) : (
            <span className="inline-flex items-center justify-center gap-1"><ImageIcon size={9} /> Replace thumb</span>
          )}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => uploadFile(e.target.files?.[0], "thumbnail")}
            className="hidden"
          />
        </label>
      </div>

      {/* Form */}
      <div className="flex flex-col gap-2">
        <input
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          placeholder="Episode title"
          data-testid={`kh-admin-title-${ep.episode_number}`}
          className="w-full bg-transparent border-b border-white/10 focus:border-gold-400 text-white font-serif text-lg py-1 outline-none"
        />
        <textarea
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          placeholder="Short description"
          rows={2}
          data-testid={`kh-admin-desc-${ep.episode_number}`}
          className="w-full bg-transparent border border-white/10 focus:border-gold-400/50 text-white/80 text-sm p-2 outline-none resize-none"
        />
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/60">
            <Calendar size={12} className="text-gold-400" /> Air date
            <input
              type="date"
              value={draft.air_date || ""}
              onChange={(e) => setDraft({ ...draft, air_date: e.target.value })}
              data-testid={`kh-admin-air-date-${ep.episode_number}`}
              className="bg-ink border border-gold-500/20 text-white text-xs px-2 py-1 outline-none normal-case tracking-normal"
            />
          </label>
          <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/60 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.is_featured}
              onChange={(e) => setDraft({ ...draft, is_featured: e.target.checked })}
              data-testid={`kh-admin-featured-${ep.episode_number}`}
              className="accent-gold-500"
            />
            <Sparkles size={11} className="text-gold-400" /> Featured
          </label>
          {ep.video_url && (
            <a
              href={ep.video_url.startsWith("http") ? ep.video_url : `${process.env.REACT_APP_BACKEND_URL}${ep.video_url}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`kh-admin-preview-${ep.episode_number}`}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-gold-400 hover:text-gold-300"
            >
              <Play size={11} /> Preview
            </a>
          )}
          <div className="ml-auto flex items-center gap-2">
            <label
              data-testid={`kh-admin-video-upload-${ep.episode_number}`}
              className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] px-3 py-2 cursor-pointer transition-colors ${
                uploading === "video"
                  ? "bg-gold-500/20 text-gold-300"
                  : "border border-gold-500/40 text-gold-300 hover:bg-gold-500/10"
              }`}
            >
              {uploading === "video" ? (
                <><Loader2 size={12} className="animate-spin" /> {progress}%</>
              ) : (
                <><UploadCloud size={12} /> {isReady ? "Replace video" : "Upload MP4"}</>
              )}
              <input
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
                onChange={(e) => uploadFile(e.target.files?.[0], "video")}
                className="hidden"
              />
            </label>
            <button
              onClick={saveMeta}
              disabled={!dirty || saving}
              data-testid={`kh-admin-save-${ep.episode_number}`}
              className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-3 py-2 transition-colors ${
                dirty && !saving
                  ? "bg-gold-500 hover:bg-gold-400 text-ink font-semibold"
                  : "border border-white/10 text-white/30 cursor-not-allowed"
              }`}
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Save
            </button>
          </div>
        </div>
        {uploading === "video" && (
          <div className="w-full h-1 bg-white/5 overflow-hidden" data-testid={`kh-admin-progress-${ep.episode_number}`}>
            <div className="h-full bg-gold-500 transition-[width]" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
    </motion.div>
  );
}
